/** @jsx jsx */

import type { MouseEvent as ReactMouseEvent } from 'react';

import { css, jsx } from '@emotion/react';
import { Button } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

import { SIDEBAR_ID } from '../constants';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../../../constants';
import { Divider } from '../Divider';
import OutOfAppLinks from './OutOfAppLinks';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../hooks';

import type { CallToAction } from '../../../constants';
import type { NavItem } from '../../../constants/links';

const GAP = 20;

const FooterStyles = {
  sidebarCtaButtons: css`
    display: grid;
    grid-template-columns: 1fr fr;
    gap: 10px;
  `,
};

const Styles = labelStyles('OutOfAppSidebar', {
  root: ({
    isCollapsed,
    isFullWidth,
    isShadowed,
  }: Pick<
    OutOfAppSidebarProps,
    'isCollapsed' | 'isFullWidth' | 'isShadowed'
  >) => [
    {
      position: 'relative',
      // Create grid to stretch content so that it scrolls.
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
      padding: '30px 10px 0 10px',
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
    },

    !isFullWidth &&
      withModifier('constrainedWidth', {
        width: UNIVERSAL_SIDEBAR_WIDTH,
        maxWidth: '100vw',
      }),

    !isFullWidth &&
      withModifier('bordered', {
        // Add a border-right w/o the border actually taking
        // up any box model space. border-right reduces the inner
        // width of the content and causes horizontal scrolling
        // which makes this necessary.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 1,
          backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
        },
      }),

    // Sidebar shadow. Using a pseudo element instead of `box-shadow`
    // so the shadow only goes in one direction.
    isShadowed &&
      withModifier('shadowed', {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '100%',
          bottom: 0,
          width: 10,
          pointerEvents: 'none',
          background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
        },
      }),

    isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
      }),
  ],

  header: {
    flex: 'none',
  },

  content: ({
    isCollapsed,
    isV3,
  }: Pick<OutOfAppSidebarProps, 'isCollapsed' | 'isV3'>) => [
    {
      overflow: 'auto',
      paddingTop: isV3 ? 5 : GAP,
      paddingBottom: 5,
      flexBasis: 'auto',
      flexShrink: 1,
      flexGrow: 0,

      // Hide scrollbar, especially on Windows and in Firefox on macOS.
      // Shouldn't be a usability issue since a) scrolling is unlikely
      // to be necessary for most screens, and b) scrolling should still
      // be obvious due to clipped content.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    isCollapsed &&
      withModifier('collapsed', {
        overflow: 'visible',
      }),
  ],

  links: ({
    isCollapsed,
    isV3,
  }: Pick<OutOfAppSidebarProps, 'isCollapsed' | 'isV3'>) => [
    {
      alignContent: 'start',
      display: 'grid',
      gap: isV3 ? 5 : 10,
      listStyle: 'none',
      ...(!isCollapsed && { padding: '0 2px' }),
    },
  ],

  footer: {
    flex: 'none',
    paddingBottom: GAP,
  },
});

export type OutOfAppSidebarProps = {
  callToAction?: CallToAction;
  currentUrl: string;
  isActuallyCollapsed: boolean;
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean;
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth?: boolean;
  isLoggedIn: boolean;
  isOnAppPage: boolean;
  /**
   * Whether the sidebar has a shadow. Should only be used when
   * the sidebar is floating.
   */
  isShadowed?: boolean;
  /**
   * If true, adjusts layout for v3 sidebar links
   */
  isV3?: boolean;
  loginHref: string;
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (e: ReactMouseEvent) => void;
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (e: ReactMouseEvent) => void;
  // TODO: Add description
  outOfAppLinks: Array<NavItem>;
  signupHref: string;
  size: 'small' | 'medium';
};

/**
 * @private
 * Sidebar to render when a user is on an out-of-app page (regardless of whether they are logged in or not). Includes:
 * - links that would be rendered in the topbar (if size permitted)
 * - authentication links (if user is logged out)
 * - Call to Action (if any)
 */
const OutOfAppSidebar = ({
  callToAction,
  currentUrl,
  isActuallyCollapsed,
  isCollapsed = false,
  isFullWidth = true,
  isOnAppPage,
  isLoggedIn,
  isShadowed,
  isV3 = false,
  loginHref,
  onClick,
  onMouseEnter,
  onMouseLeave,
  outOfAppLinks,
  signupHref,
  size,
}: OutOfAppSidebarProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const mouseProps = {
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  const Footer = () => {
    const LOGIN_TEXT = 'Log in';
    const MY_ZAPS_TEXT = 'My Zaps';
    const SIGN_UP_TEXT = 'Sign up';
    const ZAPS_URL = getFullyQualifiedURL('/app/zaps');
    const LOGIN_URL = getFullyQualifiedURL(loginHref);
    const SIGNUP_URL = getFullyQualifiedURL(signupHref);

    const callToActionUrl =
      getFullyQualifiedURL(
        `${callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
      ) || '';

    const handleCallToActionClick = (text?: string) => {
      emitUserInteractionEvent({
        event_action: 'click',
        option_selected: `UniversalDynamicTopbar: ${text}`,
      });
    };

    if (size === 'small' && !isActuallyCollapsed) {
      if (!isLoggedIn) {
        return (
          <div css={Styles.footer} role="region" data-zds>
            <Divider />
            <div css={FooterStyles.sidebarCtaButtons} data-zds>
              {callToAction && (
                <Button
                  color="secondary"
                  href={callToActionUrl}
                  onClick={() =>
                    handleCallToActionClick(
                      `${callToAction?.text} to ${callToActionUrl}`
                    )
                  }
                  size="small"
                >
                  {callToAction.text}
                </Button>
              )}
              <Button
                color="secondary"
                href={LOGIN_URL}
                size="small"
                onClick={() =>
                  handleCallToActionClick(`${LOGIN_TEXT} ${LOGIN_URL}`)
                }
              >
                {LOGIN_TEXT}
              </Button>
              <Button
                color="primary"
                href={SIGNUP_URL}
                size="small"
                onClick={() =>
                  handleCallToActionClick(
                    `${SIGN_UP_TEXT} ${SIGNUP_URL}
                      )}`
                  )
                }
              >
                {SIGN_UP_TEXT}
              </Button>
            </div>
          </div>
        );
      }
      if (isLoggedIn && !isOnAppPage) {
        return (
          <div css={Styles.footer} role="region" data-zds>
            <Divider />
            <div css={FooterStyles.sidebarCtaButtons} data-zds>
              {callToAction && (
                <Button
                  color="secondary"
                  href={callToActionUrl}
                  isFullWidth={true}
                  size="medium"
                  onClick={() =>
                    handleCallToActionClick(
                      `${callToAction?.text} to ${callToActionUrl}`
                    )
                  }
                >
                  {callToAction.text}
                </Button>
              )}
              <Button
                color="primary-alt"
                href={ZAPS_URL}
                isFullWidth={true}
                size="medium"
                onClick={() =>
                  handleCallToActionClick(`${MY_ZAPS_TEXT} ${ZAPS_URL}`)
                }
              >
                {MY_ZAPS_TEXT}
              </Button>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <nav
      aria-label="Site Links"
      css={Styles.root({ isCollapsed, isFullWidth, isShadowed })}
      data-testid="universalSidebar"
      id={SIDEBAR_ID}
      {...mouseProps}
      data-zds
    >
      <div css={Styles.content({ isCollapsed, isV3 })} data-zds>
        <ul css={Styles.links({ isCollapsed, isV3 })} data-zds>
          <OutOfAppLinks
            isCollapsed={isCollapsed}
            navigationItems={outOfAppLinks}
          />
        </ul>
      </div>

      <Footer />
    </nav>
  );
};

export default OutOfAppSidebar;
